// Common in-app browser tokens (LinkedIn, Instagram, Facebook, etc.)
const EMBEDDED_UA_REGEX = /(FBAN|FBAV|FB_IAB|FB4A|Instagram|LinkedInApp|LinkedIn|LIAPP|Snapchat|Line|KAKAOTALK|Kuaishou|TikTok|WeChat|QQBrowser|Weibo|wv|WebView|GSA)/i;

const matchesEmbeddedBrand = (brands = []) => brands.some((brand) => /Instagram|Facebook|LinkedIn|TikTok|WebView/i.test(brand.brand));

export const isEmbeddedBrowser = () => {
    if (typeof navigator === 'undefined') {
        return false;
    }

    const userAgent = navigator.userAgent || '';
    const vendor = navigator.vendor || '';
    const brands = navigator.userAgentData?.brands || [];

    return (
        EMBEDDED_UA_REGEX.test(userAgent)
        || /; wv\)/i.test(userAgent)
        || (/iPhone|iPad|iPod/i.test(userAgent) && /AppleWebKit(?!.*Safari)/i.test(userAgent))
        || /GoogleApp/i.test(vendor)
        || matchesEmbeddedBrand(brands)
    );
};

const normalizeUrl = (value) => {
    if (typeof window === 'undefined') {
        return value;
    }

    if (!value) {
        return window.location.href;
    }

    if (/^https?:\/\//i.test(value)) {
        return value;
    }

    if (value.startsWith('/')) {
        return `${window.location.origin}${value}`;
    }

    return `${window.location.origin}/${value}`;
};

const ensureExternalBrowserParam = (value) => {
    try {
        const url = new URL(value);
        if (url.searchParams.get('openExternalBrowser') === '1') {
            return url.toString();
        }
        url.searchParams.set('openExternalBrowser', '1');
        return url.toString();
    } catch (_error) {
        if (!value) {
            return value;
        }
        if (/[?&]openExternalBrowser=1/i.test(value)) {
            return value;
        }
        const separator = value.includes('?') ? '&' : '?';
        return `${value}${separator}openExternalBrowser=1`;
    }
};

const buildChromeSchemeUrl = (value) => {
    try {
        const url = new URL(value);
        const scheme = url.protocol === 'https:' ? 'googlechromes:' : 'googlechrome:';
        return `${scheme}//${url.host}${url.pathname}${url.search}${url.hash}`;
    } catch (_error) {
        return null;
    }
};

export const openInSystemBrowser = (url, options = {}) => {
    if (typeof window === 'undefined') {
        return false;
    }

    const { forceExternalParam = false } = options;

    const normalizedUrl = normalizeUrl(url);
    const targetUrl = forceExternalParam ? ensureExternalBrowserParam(normalizedUrl) : normalizedUrl;
    const { userAgent = '' } = navigator;
    const isIOS = /iP(hone|od|ad)/i.test(userAgent);
    const isAndroid = /Android/i.test(userAgent);

    const openWithAnchor = () => {
        if (typeof document === 'undefined') {
            return false;
        }

        try {
            const { body } = document;
            if (!body) {
                return false;
            }

            const anchor = document.createElement('a');
            anchor.href = targetUrl;
            anchor.target = '_blank';
            anchor.rel = 'noopener noreferrer external';
            anchor.style.display = 'none';
            body.appendChild(anchor);
            anchor.click();
            body.removeChild(anchor);
            return true;
        } catch (_error) {
            return false;
        }
    };

    const openWithChromeScheme = () => {
        if (!isIOS) {
            return false;
        }

        const chromeUrl = buildChromeSchemeUrl(normalizedUrl);
        if (!chromeUrl) {
            return false;
        }

        try {
            window.location.href = chromeUrl;
            return true;
        } catch (_error) {
            return false;
        }
    };

    const openNewWindow = () => {
        const newWindow = window.open(targetUrl, '_blank', 'noopener,noreferrer');
        if (newWindow) {
            newWindow.focus?.();
            return true;
        }
        return false;
    };

    const openWithIntent = () => {
        try {
            const sanitized = normalizedUrl.replace(/^https?:\/\//i, '');
            const scheme = normalizedUrl.startsWith('https://') ? 'https' : 'http';
            const intentUrl = `intent://${sanitized}#Intent;scheme=${scheme};package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(normalizedUrl)};end`;
            window.location.href = intentUrl;
            return true;
        } catch (_error) {
            return false;
        }
    };

    if (isIOS) {
        if (openWithChromeScheme() || openWithAnchor() || openNewWindow()) {
            return true;
        }
    }

    if (isAndroid) {
        if (openWithIntent() || openWithAnchor() || openNewWindow()) {
            return true;
        }
    }

    if (openWithAnchor() || openNewWindow()) {
        return true;
    }

    try {
        navigator.clipboard?.writeText?.(normalizedUrl);
    } catch (_error) {
        // ignore clipboard errors
    }

    return false;
};

export const requestEmbeddedBrowserNotice = (detail = {}) => {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function' || typeof window.CustomEvent !== 'function') {
        return false;
    }

    try {
        window.dispatchEvent(new CustomEvent('embedded-browser:show', { detail }));
        return true;
    } catch (_error) {
        return false;
    }
};
